import { useState, useCallback, useMemo } from 'react';
import { ProfileVortex } from '../../components/ProfileVortex';
import { TabbedNotes } from '../../components/TabbedNotes';
import { GaugeCheck } from '../../components/GaugeCheck';
import { VortexLayout } from '../../components/VortexLayout';
import { Modal } from '../../components/Modal';
import {
  mockDrivers,
  getNotesForDriver,
  getGaugeItemsForDriver,
} from '../../services/mockData';
import type { Note, GaugeItem } from '../../types/driver';
import './home-page.css';

/**
 * HomePage component
 *
 * Main dashboard layout featuring the vortex-centric design where:
 * - Branding pill floats at the top
 * - Profile vortex floats in the center
 * - Dock floats at the bottom
 * - All panels flow around these three floating islands
 */
export function HomePage(): JSX.Element {
  // Driver selection state (would come from route params or context in real app)
  const [currentDriverId] = useState(mockDrivers[0].id);

  // Modal states
  const [isAddNoteModalOpen, setIsAddNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);

  // Get current driver data
  const currentDriver = useMemo(
    () => mockDrivers.find((d) => d.id === currentDriverId) ?? mockDrivers[0],
    [currentDriverId]
  );

  // Get notes for current driver
  const [driverNotes] = useState<Note[]>(() =>
    getNotesForDriver(currentDriverId)
  );

  // Get GAUGE ITEMS for current driver
  const [GaugeItems, setGaugeItems] = useState<GaugeItem[]>(() =>
    getGaugeItemsForDriver(currentDriverId)
  );

  // Handle add note
  const handleAddNote = useCallback(() => {
    setIsAddNoteModalOpen(true);
  }, []);

  // Handle note click
  const handleNoteClick = useCallback((note: Note) => {
    setSelectedNote(note);
  }, []);

  // Handle gauge item toggle
  const handleToggleGaugeItem = useCallback((itemId: string, completed: boolean) => {
    setGaugeItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              status: completed ? 'completed' : 'pending',
              lastCompletedAt: completed ? new Date().toISOString() : item.lastCompletedAt,
              lastCompletedBy: completed ? 'Current User' : item.lastCompletedBy,
            }
          : item
      )
    );
  }, []);

  // Adapt driver status for ProfileVortex
  const vortexDriver = useMemo(() => ({
    photoUrl: currentDriver.photoUrl ?? undefined,
    displayName: currentDriver.displayName,
    status: currentDriver.status === 'active' ? 'active' as const : 'inactive' as const,
  }), [currentDriver]);

  return (
    <div className="home-page">
      {/* Vortex-centric unified layout */}
      <VortexLayout
        vortex={<ProfileVortex driver={vortexDriver} />}
        notesContent={
          <TabbedNotes
            notes={driverNotes}
            onNoteClick={handleNoteClick}
            onAddNote={handleAddNote}
          />
        }
        tasksContent={
          <GaugeCheck
            items={GaugeItems}
            onToggleItem={handleToggleGaugeItem}
          />
        }
      />

      {/* Add Note Modal */}
      <Modal
        isOpen={isAddNoteModalOpen}
        onClose={() => setIsAddNoteModalOpen(false)}
        title="Add Note"
        size="md"
      >
        <div className="add-note-form">
          <p className="add-note-placeholder">
            Note creation form will be implemented in a future iteration.
            This modal demonstrates the dock-aware backdrop behavior.
          </p>
        </div>
      </Modal>

      {/* View Note Modal */}
      <Modal
        isOpen={selectedNote !== null}
        onClose={() => setSelectedNote(null)}
        title={selectedNote?.category ? `${selectedNote.category} Note` : 'Note Details'}
        size="md"
      >
        {selectedNote && (
          <div className="view-note-content">
            <div className="view-note-meta">
              <span className={`note-category category-${selectedNote.category}`}>
                {selectedNote.category}
              </span>
              {selectedNote.priority !== 'low' && (
                <span className={`note-priority priority-${selectedNote.priority}`}>
                  {selectedNote.priority}
                </span>
              )}
            </div>
            <p className="view-note-text">{selectedNote.content}</p>
            {selectedNote.tags.length > 0 && (
              <div className="view-note-tags">
                {selectedNote.tags.map((tag) => (
                  <span key={tag} className="note-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}
            <div className="view-note-footer">
              <span className="view-note-author">{selectedNote.createdByName}</span>
              <span className="view-note-date">
                {new Date(selectedNote.createdAt).toLocaleDateString()}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default HomePage;
